import { atom } from "jotai";
import { atomWithRefresh, loadable } from "jotai/utils";

import { Logger } from "@moment/logging";

import { env } from "../env";
import type { EnvFetchResponse } from "../request-page";
import type { RecentUpdate } from "./recent-updates.types";

const logger = new Logger("recent-updates");

export const RECENT_UPDATES_FEED_URL =
	"https://docs.moment.dev/api/rss/moment/7a73pjwdpgq6kukdaozvqp55y";

export const RECENT_UPDATES_ACKNOWLEDGED_AT_STORAGE_KEY = "moment:recent-updates-acknowledged-at";

export const parseRecentUpdatesFeed = (xml: string): RecentUpdate[] => {
	if (typeof DOMParser === "undefined") {
		return [];
	}

	const document = new DOMParser().parseFromString(xml, "application/xml");
	const parserError = document.querySelector("parsererror");
	if (parserError) {
		throw new Error(parserError.textContent ?? "Invalid RSS feed");
	}

	return [...document.querySelectorAll("item")]
		.map((item) => {
			const title = item.querySelector("title")?.textContent?.trim();
			const link = item.querySelector("link")?.textContent?.trim();
			const pubDate = item.querySelector("pubDate")?.textContent?.trim();

			if (!title || !link || !pubDate) {
				return null;
			}

			const publishedAt = new Date(pubDate);
			if (Number.isNaN(publishedAt.getTime())) {
				return null;
			}

			return {
				title,
				link,
				publishedAt,
			} satisfies RecentUpdate;
		})
		.filter((item): item is RecentUpdate => item !== null)
		.sort((left, right) => right.publishedAt.getTime() - left.publishedAt.getTime());
};

type RecentUpdatesFetchInput = {
	request: {
		method: string;
		url: string;
		params: { enabled?: boolean; key: string; value: string }[];
		headers: { enabled?: boolean; key: string; value: string }[];
		auth: { type: "none" };
		body: {
			mode: string;
			text: string;
			form: { enabled?: boolean; key: string; value: string }[];
		};
		timeoutMs: number;
		followRedirects: boolean;
		validateSSL: boolean;
	};
	environment_name?: string | null;
	document_id?: string | null;
	env_vars?: Record<string, string>;
};

const fetchRecentUpdatesXml = async (headers: HeadersInit): Promise<string> => {
	if (env.isDesktopApp) {
		const { invoke } = await import("@tauri-apps/api/core");
		const input: RecentUpdatesFetchInput = {
			request: {
				method: "GET",
				url: RECENT_UPDATES_FEED_URL,
				params: [],
				headers: Object.entries(headers).map(([key, value]) => ({
					enabled: true,
					key,
					value,
				})),
				auth: { type: "none" },
				body: { mode: "none", text: "", form: [] },
				timeoutMs: 30000,
				followRedirects: true,
				validateSSL: true,
			},
			environment_name: null,
			document_id: null,
			env_vars: {},
		};

		const response = await invoke<EnvFetchResponse>("execute_env_fetch", { input });
		if (response.status < 200 || response.status >= 300) {
			throw new Error(`Failed to fetch recent updates feed: ${response.status}`);
		}

		return response.body ?? "";
	}

	const response = await fetch(RECENT_UPDATES_FEED_URL, {
		method: "GET",
		headers,
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch recent updates feed: ${response.status}`);
	}

	return response.text();
};

export const fetchRecentUpdates = async (): Promise<RecentUpdate[]> => {
	if (env.isSsr || !env.isDesktopApp || typeof fetch === "undefined") {
		return [];
	}

	const headers: HeadersInit = {};
	if (env.userAgent) {
		headers["X-Moment-User-Agent"] = env.userAgent;
	}

	return parseRecentUpdatesFeed(await fetchRecentUpdatesXml(headers));
};

export const $recentUpdatesFeed = atomWithRefresh(async () => {
	try {
		return await fetchRecentUpdates();
	} catch (error) {
		logger.warn("Failed to fetch recent updates", { error });
		return [];
	}
});
$recentUpdatesFeed.debugLabel = "recentUpdatesFeed";

const $cachedRecentUpdatesAcknowledgedAt = atomWithRefresh(async () => {
	if (typeof window === "undefined") {
		return null;
	}

	const rawAcknowledgedAt = window.localStorage.getItem(
		RECENT_UPDATES_ACKNOWLEDGED_AT_STORAGE_KEY
	);
	if (!rawAcknowledgedAt) {
		return null;
	}

	const acknowledgedAt = new Date(rawAcknowledgedAt);
	return Number.isNaN(acknowledgedAt.getTime()) ? null : acknowledgedAt;
});
$cachedRecentUpdatesAcknowledgedAt.debugLabel = "cachedRecentUpdatesAcknowledgedAt";

export const $recentUpdatesAcknowledgedAt = atom(
	async (get) => get($cachedRecentUpdatesAcknowledgedAt),
	async (_get, set, acknowledgedAt: Date | null) => {
		if (typeof window !== "undefined") {
			if (acknowledgedAt) {
				window.localStorage.setItem(
					RECENT_UPDATES_ACKNOWLEDGED_AT_STORAGE_KEY,
					acknowledgedAt.toISOString()
				);
			} else {
				window.localStorage.removeItem(RECENT_UPDATES_ACKNOWLEDGED_AT_STORAGE_KEY);
			}
		}

		set($cachedRecentUpdatesAcknowledgedAt);
	}
);
$recentUpdatesAcknowledgedAt.debugLabel = "recentUpdatesAcknowledgedAt";

export const $unacknowledgedRecentUpdates = atom(async (get) => {
	const [updates, acknowledgedAt] = await Promise.all([
		get($recentUpdatesFeed),
		get($cachedRecentUpdatesAcknowledgedAt),
	]);

	if (!acknowledgedAt) {
		return updates;
	}

	return updates.filter((update) => update.publishedAt > acknowledgedAt);
});
$unacknowledgedRecentUpdates.debugLabel = "unacknowledgedRecentUpdates";

export const $loadableUnacknowledgedRecentUpdates = loadable($unacknowledgedRecentUpdates);
$loadableUnacknowledgedRecentUpdates.debugLabel = "loadableUnacknowledgedRecentUpdates";

export const $acknowledgeRecentUpdates = atom(null, async (get, set, updates?: RecentUpdate[]) => {
	const pendingUpdates = updates ?? (await get($unacknowledgedRecentUpdates));
	if (pendingUpdates.length === 0) {
		return;
	}

	const mostRecentUpdate = pendingUpdates.reduce((latest, current) =>
		current.publishedAt > latest.publishedAt ? current : latest
	);

	await set($recentUpdatesAcknowledgedAt, mostRecentUpdate.publishedAt);
});
$acknowledgeRecentUpdates.debugLabel = "acknowledgeRecentUpdates";
